import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { RecipeDraft } from '@familyhub/shared';
import { useSession } from '../session';
import { useHoldUpdates } from '../platform/update';
import { ApiError, isOffline } from '../api/client';
import { importRecipe, storeRecipeImage } from './import';
import { captureImages, cropCapturedImage, type CapturedImage, type CropBox } from './capture';
import { RecipeSheet } from './RecipeSheet';
import type { RecipeInput } from './actions';
import { Thinking } from '../components/Loading';

/**
 * The frictionless add screen: import a recipe from a photo / app screenshots, a
 * URL, or pasted text, then review the parsed draft and save. Import is online
 * (it needs the model); the review + save go through the module's own offline
 * outbox like any other write. Manual entry is always available as the fallback.
 */

type Mode = 'photo' | 'url' | 'text';

/**
 * If the importer located a finished-dish photo inside one of the uploaded
 * screenshots, cut it out here — where the original bytes live — and store it as
 * the display picture, so the app shows the food and not a centre-cropped slab of
 * screenshot. Best-effort: any failure leaves the server's `image_path` (the full
 * screenshot) in place.
 */
async function withDishCrop(draft: RecipeDraft, images: CapturedImage[]): Promise<RecipeDraft> {
  const { image_index: i, image_box: box } = draft;
  if (i == null || box == null || !images[i]) return draft;
  try {
    const cropped = await cropCapturedImage(images[i], box);
    const path = await storeRecipeImage(cropped);
    return { ...draft, image_path: path };
  } catch {
    return draft;
  }
}

function draftToInput(d: RecipeDraft): RecipeInput {
  return {
    title: d.title,
    servings: d.servings,
    total_minutes: d.total_minutes,
    ingredients: d.ingredients,
    steps: d.steps,
    tags: d.tags,
    source_type: d.source_type,
    source_url: d.source_url,
    source_name: d.source_name,
    image_path: d.image_path,
    source_images: d.source_images,
    notes: null,
  };
}

export function RecipeImportScreen() {
  const navigate = useNavigate();
  const session = useSession();
  const userId = session.status === 'in' ? session.userId : null;

  const [mode, setMode] = useState<Mode>('photo');
  const [images, setImages] = useState<CapturedImage[]>([]);
  const [preparing, setPreparing] = useState(false);
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [status, setStatus] = useState<'idle' | 'importing' | 'error' | 'offline'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [seed, setSeed] = useState<RecipeInput | null>(null);
  const [cropSource, setCropSource] = useState<{ image: CapturedImage; box: CropBox | null } | null>(null);
  const [manual, setManual] = useState(false);

  // An import in flight is a photo and a paid model call that exist nowhere yet;
  // a deploy applying itself here would throw both away. The draft it produces
  // needs no hold — it is shown in a sheet, which `safeToReload` already waits on.
  useHoldUpdates(preparing || status === 'importing');

  const onFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setPreparing(true);
    setStatus('idle');
    try {
      setImages(await captureImages(files));
    } catch (err) {
      setErrorMsg(
        err instanceof Error ? `Kon de foto niet verwerken: ${err.message}` : 'Kon de foto niet verwerken.',
      );
      setStatus('error');
    } finally {
      setPreparing(false);
    }
  };

  const canImport =
    (mode === 'photo' && images.length > 0) ||
    (mode === 'url' && url.trim().length > 0) ||
    (mode === 'text' && text.trim().length > 0);

  const runImport = async () => {
    setStatus('importing');
    setErrorMsg('');
    try {
      const body =
        mode === 'photo'
          ? { kind: 'photo' as const, images }
          : mode === 'url'
            ? { kind: 'url' as const, url: url.trim() }
            : { kind: 'text' as const, text: text.trim() };
      const { draft } = await importRecipe(body);
      const sourceImages = mode === 'photo' ? images : [];
      setSeed(draftToInput(await withDishCrop(draft, sourceImages)));
      // Keep the original screenshot behind the picture so the reviewer can re-crop
      // the whole shot, not just the auto-cropped result.
      const { image_index: i, image_box: box } = draft;
      setCropSource(i != null && sourceImages[i] ? { image: sourceImages[i], box } : null);
      setStatus('idle');
    } catch (err) {
      if (isOffline(err)) setStatus('offline');
      else {
        setErrorMsg(err instanceof ApiError ? err.message : 'Er ging iets mis.');
        setStatus('error');
      }
    }
  };

  return (
    <div className="app">
      <div className="topbar">
        <Link className="btn btn-quiet back" to="/recepten" aria-label="Terug">‹ Recepten</Link>
        <h1>Recept toevoegen</h1>
        <span style={{ width: 52 }} aria-hidden />
      </div>

      <main className="import-main">
        <div className="import-modes" role="tablist" aria-label="Manier van toevoegen">
          <button className={`import-mode${mode === 'photo' ? ' on' : ''}`} role="tab" aria-selected={mode === 'photo'} onClick={() => setMode('photo')}>Foto</button>
          <button className={`import-mode${mode === 'url' ? ' on' : ''}`} role="tab" aria-selected={mode === 'url'} onClick={() => setMode('url')}>Link</button>
          <button className={`import-mode${mode === 'text' ? ' on' : ''}`} role="tab" aria-selected={mode === 'text'} onClick={() => setMode('text')}>Tekst</button>
        </div>

        {mode === 'photo' && (
          <div className="import-panel">
            <p className="sheet-note">Foto van een kookboek of schermafbeeldingen (bijv. Crisp) — meerdere mogen.</p>
            <label className="btn btn-outline btn-lg import-file">
              {images.length ? `${images.length} ${images.length === 1 ? 'foto' : "foto's"} gekozen` : "Foto's kiezen"}
              <input type="file" accept="image/*" multiple onChange={(e) => void onFiles(e.target.files)} hidden />
            </label>
          </div>
        )}

        {mode === 'url' && (
          <div className="import-panel">
            <input type="url" inputMode="url" placeholder="https://…" value={url} onChange={(e) => setUrl(e.target.value)} aria-label="Recept-link" />
          </div>
        )}

        {mode === 'text' && (
          <div className="import-panel">
            <textarea rows={8} placeholder="Titel, ingrediënten en stappen…" value={text} onChange={(e) => setText(e.target.value)} aria-label="Recepttekst" />
          </div>
        )}

        {status === 'offline' && <p className="sheet-note error">Geen verbinding — importeren lukt alleen online.</p>}
        {status === 'error' && <p className="sheet-note error">{errorMsg}</p>}

        <button
          className="btn btn-primary btn-lg import-go"
          onClick={() => void runImport()}
          disabled={!canImport || preparing || status === 'importing'}
        >
          {status === 'importing' ? 'Recept lezen…' : preparing ? "Foto's verwerken…" : 'Importeer'}
        </button>

        {(preparing || status === 'importing') && (
          <Thinking
            messages={
              preparing
                ? ["De foto's verwerken…"]
                : mode === 'url'
                  ? ['De pagina ophalen…', 'Het recept eruit halen…', 'Bijna klaar…']
                  : mode === 'text'
                    ? ['De tekst lezen…', 'Het recept ordenen…']
                    : ['De foto lezen…', 'Ingrediënten herkennen…', 'Het recept overtypen…']
            }
          />
        )}

        <button className="btn btn-quiet import-manual" onClick={() => setManual(true)}>
          Of voer handmatig in
        </button>

        <Link className="btn btn-quiet import-manual" to="/recepten/genereren">
          Of genereer recepten uit je pakket
        </Link>
      </main>

      {seed && (
        <RecipeSheet
          recipe={null}
          seed={seed}
          cropSource={cropSource}
          userId={userId}
          onClose={() => { setSeed(null); setCropSource(null); }}
          onSaved={(id) => navigate(`/recepten/${id}`)}
        />
      )}
      {manual && (
        <RecipeSheet
          recipe={null}
          userId={userId}
          onClose={() => setManual(false)}
          onSaved={(id) => navigate(`/recepten/${id}`)}
        />
      )}
    </div>
  );
}
